import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, EmptyState, ErrorState, Spinner, Table, Td, Th } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import type { ApiException } from '@/lib/api/types';
import * as adminApi from './api';
import type { ErrorLogItem, ErrorLogOverview } from './types';

/**
 * 5xx 오류 이력. (00-architecture.md 8절 Errors 지표)
 *
 * 🔴 왜 만들었나
 *   홈서버라 로그 파일을 보려면 SSH 로 들어가 docker logs 를 봐야 한다.
 *   폰으로 "지금 뭔가 터졌나" 를 확인할 방법이 없었다.
 *
 * 🔴 요약을 목록보다 위에 둔다
 *   같은 오류가 200번 났는데 목록에서는 200줄로만 보인다.
 *   묶어야 "이것 하나만 고치면 된다" 가 드러난다.
 *
 * 🔴 여기 없는 것은 응답에서 뺀 것이 아니라 DB 에 없다
 *   요청 본문 · 쿼리 문자열 · 로그인 아이디를 애초에 저장하지 않는다.
 *   본문에는 잔고·보유종목·계좌번호가, 쿼리에는 인증키가 들어올 수 있다. (CLAUDE.md 1.2절)
 */

const PAGE_SIZE = 20;

export function ErrorLogPanel() {
  const [page, setPage] = useState(0);

  const query = useQuery<ErrorLogOverview, ApiException>({
    queryKey: ['admin', 'errors', page],
    queryFn: () => adminApi.fetchErrorLogs(page, PAGE_SIZE),
    // 오류는 자주 나지 않는다. 짧게 잡을 이유가 없다.
    staleTime: 60 * 1000,
  });

  if (query.isLoading) return <Spinner />;
  if (query.error) {
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }
  if (!query.data) return null;

  const data = query.data;

  return (
    <div className="space-y-4">
      <Card title="오류 요약">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="최근 24시간" value={data.last24h} highlight={data.last24h > 0} />
          <Stat label="최근 7일" value={data.last7d} highlight={false} />
        </div>

        {data.grouped.length > 0 && (
          <div className="mt-4">
            <p className="mb-1 text-xs text-fg-subtle">
              최근 7일 예외 종류별 — 많은 순
            </p>
            <ul className="space-y-1">
              {data.grouped.map((group) => (
                <li
                  key={group.exception}
                  className="flex items-baseline justify-between gap-2 text-sm"
                >
                  <span className="truncate font-medium">{group.exception}</span>
                  <span className="shrink-0 text-xs text-fg-subtle">
                    <span className="tabular">{group.count}</span>건 · 마지막{' '}
                    {formatDateTime(group.lastAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-3 text-xs text-fg-subtle">
          4xx 는 남기지 않습니다. 사용자 입력 문제라 이력으로 쌓을 값이 없습니다. 이력은 30일이
          지나면 매일 04:00 배치가 지웁니다.
        </p>
      </Card>

      <Card title={`최근 오류 ${data.totalElements}건`}>
        {data.recent.length === 0 ? (
          <EmptyState
            title="기록된 오류가 없습니다"
            description="5xx 가 한 번도 나지 않았거나, 이 기능이 배포된 뒤로 아직 없습니다."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>시각</Th>
                  <Th>예외</Th>
                  <Th>요청</Th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((item) => (
                  <ErrorRow key={item.id} item={item} />
                ))}
              </tbody>
            </Table>

            {data.totalPages > 1 && (
              <div className="mt-3 flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </Button>
                <span className="text-xs text-fg-subtle">
                  {page + 1} / {data.totalPages}
                </span>
                <Button
                  variant="secondary"
                  disabled={page + 1 >= data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  다음
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-fg-subtle">{label}</p>
      {/* 0건이면 강조하지 않는다. 붉은 숫자가 늘 떠 있으면 눈이 무뎌진다 */}
      <p className={`mt-0.5 text-2xl tabular ${highlight ? 'text-danger' : 'text-fg'}`}>{value}</p>
    </div>
  );
}

function ErrorRow({ item }: { item: ErrorLogItem }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-surface-hover"
        onClick={() => setOpen((v) => !v)}
      >
        <Td>
          <span className="text-xs tabular">{formatDateTime(item.occurredAt)}</span>
          <span className="ml-1">
            <Badge tone="danger">{item.status}</Badge>
          </span>
        </Td>
        <Td>
          <span className="font-medium">{item.exception}</span>
          {item.message && (
            <span className="block truncate text-xs text-fg-subtle">{item.message}</span>
          )}
        </Td>
        <Td>
          <span className="text-xs text-fg-subtle">
            {item.method} {item.path}
          </span>
          {item.memberId !== null && (
            <span className="block text-xs text-fg-subtle">회원 #{item.memberId}</span>
          )}
        </Td>
      </tr>
      {open && item.stackHead && (
        <tr>
          <td colSpan={3} className="bg-surface-muted px-3 py-2">
            {/* 스택은 앞 8줄만 저장한다. 전체를 넣으면 한 건에 수십 KB 가 된다 */}
            <pre className="overflow-x-auto text-xs text-fg-muted">{item.stackHead}</pre>
          </td>
        </tr>
      )}
    </>
  );
}
